import { styled } from "@mui/material/styles";
import { FormattedMessage, useIntl } from "react-intl";

import { SubmitButton } from "@/common/components/buttons/submit-button/SubmitButton";
import { FormShell } from "@/common/components/forms/FormShell";
import { PasswordField } from "@/common/components/forms/specific/password-field/PasswordField";
import { TextField } from "@/common/components/forms/text-field/TextField";
import { useSnackbar } from "@/shared/containers/SnackbarProvider/SnackbarProvider";
import { LANG_KEYS } from "@/shared/lang/lang.constants";

import {
  EAGRI_AUTH_INITIAL_VALUES,
  eagriAuthSchema,
  type EagriAuthFormValues,
} from "./eagriImport.schema";
import { CZECH_COUNTRY_CODE } from "./farmImport.schema";
import { ImportHelp } from "./ImportHelp";
import {
  getEagriAuthErrorMessageId,
  useStoreEagriAuthMutation,
} from "./signup.api";

type Props = {
  countryCode: string;
};

const EAgriAuthForm = ({ countryCode }: Props): React.ReactElement => {
  const { formatMessage, locale } = useIntl();
  const showSnackbar = useSnackbar();
  const storeEagriAuthMutation = useStoreEagriAuthMutation();

  const handleSubmit = (values: EagriAuthFormValues) => {
    storeEagriAuthMutation.mutate(
      {
        login: values.username,
        secret: values.wsKey,
        szrid: values.szrid,
      },
      {
        onError: (error) => {
          showSnackbar({
            isError: true,
            message: (
              <FormattedMessage id={getEagriAuthErrorMessageId(error)} />
            ),
          });
        },
      },
    );
  };

  const showHelp =
    locale === LANG_KEYS.CZ && countryCode === CZECH_COUNTRY_CODE;

  const help = showHelp ? (
    <ImportHelp
      copy={formatMessage({ id: "EAgriImport.howTo.copy1" })}
      linkCopy={formatMessage({ id: "common.userHelp" })}
    />
  ) : null;

  return (
    <FormShell<EagriAuthFormValues>
      initialValues={EAGRI_AUTH_INITIAL_VALUES}
      onSubmit={handleSubmit}
      validationSchema={eagriAuthSchema}
      footer={
        <SubmitButton
          data-testid="eagri-set-auth"
          pending={storeEagriAuthMutation.isPending}
        >
          <FormattedMessage id="EAgriImport.button.loadData" />
        </SubmitButton>
      }
    >
      <TextField
        autoComplete="new-password"
        fullWidth
        label={<FormattedMessage id="EAgriImport.userName" />}
        name="username"
      />
      <HelperWrapper>
        <PasswordField
          autoComplete="new-password"
          fullWidth
          label={<FormattedMessage id="EAgriImport.wsKey" />}
          name="wsKey"
        />
        {help}
      </HelperWrapper>
      <HelperWrapper>
        <TextField
          autoComplete="new-password"
          fullWidth
          label={<FormattedMessage id="EAgriImport.szrid" />}
          name="szrid"
        />
        {help}
      </HelperWrapper>
    </FormShell>
  );
};

const HelperWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

export { EAgriAuthForm };
